import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useClickAway, useUnmount } from 'react-use';
import { Show, ShowFirstMatching } from '@components/Show';
import { BlockEditProvider, useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { getUpdateRelevantFields } from '@ContractBuilder/modules/block-edit/utils/get-update-relevant-fields';
import { useSchemaStore } from '@ContractBuilder/store';
import { useUIStore } from '@ContractBuilder/store/ui.store';
import { EditorsMapProvider } from '@providers/editors-map';
import { VariationsTabsProvider } from '@providers/variations-tabs';
import { blockAuditQueries } from '@queries/fetchBlockAudit';
import { blockQueries } from '@queries/fetchBlocks';
import { isBlocksPath } from '@root/src/utils/app-paths';
import * as Sentry from '@sentry/react';
import { useEffectOnce } from '@src/hooks';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash-es';

import { UNSAVED_CHANGES_PROMPT } from '../constants';
import { LibraryBlockEditLayout } from '../views/LibraryBlockEditLayout';
import { LoadingSkeleton } from '../views/LoadingSkeleton';

const isHTMLAnchorElement = (element: HTMLElement): element is HTMLAnchorElement => {
  return element.tagName === 'A';
};

export const LibraryBlockEditController = () => {
  const { id } = useParams();
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isBlock = isBlocksPath();
  const [isActivityLogOpen, setIsActivityLogOpen] = useState(false);
  const toggleVisibilityOfActivityLog = () => setIsActivityLogOpen((prev) => !prev);

  const { data, isLoading, refetch } = useQuery({
    ...blockQueries.detail(id as string),
    enabled: !!id,
  });

  const { data: blockAudit, isLoading: isLoadingBlockAudit } = useQuery({
    ...blockAuditQueries.forBlock(id as string, data?.data?.updated_at),
    enabled: !!id && isActivityLogOpen,
  });

  const {
    schema,
    fetchSchema,
    isLoading: isSchemaLoading,
  } = useSchemaStore(({ schema, fetchSchema, isLoading }) => ({
    schema,
    fetchSchema,
    isLoading,
  }));

  useEffectOnce(() => {
    fetchSchema().catch((error) => {
      console.error('Failed to fetch schema:', error);
      Sentry.captureException(error);
    });
  });

  const {
    formValues: state,
    hasChanges,
    setEditingBlock,
    reset,
  } = useBlockEditFormStore(({ formValues, setEditingBlock, reset }) => {
    const original = getUpdateRelevantFields(data?.data);
    const next = getUpdateRelevantFields(formValues);

    return {
      formValues,
      setEditingBlock,
      reset,
      hasChanges: !isEqual(original, next),
    };
  });

  useEffect(() => {
    if (!isLoading && data) {
      setEditingBlock(data.data);
    }
  }, [isLoading, data, setEditingBlock]);

  useUnmount(() => {
    reset();
    useUIStore.getState().reset();
  });

  const canNavigate = !hasChanges;

  useClickAway(ref, (event) => {
    if (!canNavigate) {
      const target = event.target as HTMLElement;

      if (isHTMLAnchorElement(target)) {
        const userConfirmed = window.confirm(UNSAVED_CHANGES_PROMPT);

        if (userConfirmed) {
          const href = target.getAttribute('href');

          if (href) {
            navigate(href);
          }
        }
      }
    }
  });

  const handleUpdate = async () => {
    await refetch();
    await queryClient.invalidateQueries({ queryKey: blockQueries.list().queryKey });
    toast.success(`${isBlock ? 'Block' : 'Clause'} updated successfully`, { autoClose: 3000 });
  };

  return (
    <EditorsMapProvider>
      <BlockEditProvider>
        <VariationsTabsProvider>
          <ShowFirstMatching>
            <Show when={isLoading || isSchemaLoading || !data?.data || !state?.id}>
              <LoadingSkeleton />
            </Show>
            <Show when={!!schema}>
              <div className="library h-full w-full" data-testid="library-block-edit-layout" ref={ref}>
                <LibraryBlockEditLayout
                  canNavigate={canNavigate}
                  blockAudit={blockAudit?.data}
                  data={data?.data}
                  hasChanges={hasChanges}
                  isActivityLogOpen={isActivityLogOpen}
                  isLoadingBlockAudit={isLoadingBlockAudit}
                  toggleVisibilityOfActivityLog={toggleVisibilityOfActivityLog}
                  onUpdate={handleUpdate}
                />
              </div>
            </Show>
          </ShowFirstMatching>
        </VariationsTabsProvider>
      </BlockEditProvider>
    </EditorsMapProvider>
  );
};
